/**
 * ⚠️  TAX PARAMETERS — VERIFY BEFORE EACH FILING YEAR.
 *
 * Every figure the solo-tax estimator depends on lives here, and nowhere else,
 * so updating for a new year is a single-file change.
 *
 * These are ILLUSTRATIVE defaults for a US federal estimate. They are not
 * maintained in real time and may lag legislation. Before publishing or
 * relying on output, re-check each value against the current IRS figures:
 *
 *   - Standard deduction & brackets ..... IRS Rev. Proc. (annual inflation adj.)
 *   - Social Security wage base ......... SSA annual announcement
 *   - QBI thresholds .................... IRC §199A
 *   - Additional Medicare threshold ..... IRC §1401(b)(2), not inflation-indexed
 *
 * State and local taxes are out of scope entirely. So is Korean tax: a reader
 * who is tax-resident in Korea cannot use this to estimate a Korean liability,
 * and the estimator page says so.
 */

export const TAX_YEAR = 2025;

/** Set false once the constants below have been checked against IRS sources. */
export const TAX_PARAMS_NEED_REVIEW = true;

export type FilingStatus = 'single' | 'married_joint';

type Bracket = { upTo: number; rate: number };

export const TAX_PARAMS = {
  year: TAX_YEAR,

  standardDeduction: {
    single: 15_750,
    married_joint: 31_500,
  } satisfies Record<FilingStatus, number>,

  /** Ordinary income brackets, applied to taxable income. */
  brackets: {
    single: [
      { upTo: 11_925, rate: 0.1 },
      { upTo: 48_475, rate: 0.12 },
      { upTo: 103_350, rate: 0.22 },
      { upTo: 197_300, rate: 0.24 },
      { upTo: 250_525, rate: 0.32 },
      { upTo: 626_350, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
    married_joint: [
      { upTo: 23_850, rate: 0.1 },
      { upTo: 96_950, rate: 0.12 },
      { upTo: 206_700, rate: 0.22 },
      { upTo: 394_600, rate: 0.24 },
      { upTo: 501_050, rate: 0.32 },
      { upTo: 751_600, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
  } satisfies Record<FilingStatus, Bracket[]>,

  /* ------------------------------- Payroll -------------------------------- */
  socialSecurityWageBase: 176_100,
  socialSecurityRate: 0.124, // combined employer + employee
  medicareRate: 0.029, // combined employer + employee
  additionalMedicareRate: 0.009, // employee only, above threshold
  additionalMedicareThreshold: {
    single: 200_000,
    married_joint: 250_000,
  } satisfies Record<FilingStatus, number>,

  /** Self-employment tax applies to 92.35% of net profit. */
  seTaxableShare: 0.9235,

  /* --------------------------------- QBI ---------------------------------- */
  qbiRate: 0.2,
  /** Above this taxable income the §199A limitations begin phasing in. */
  qbiPhaseInStart: {
    single: 197_300,
    married_joint: 394_600,
  } satisfies Record<FilingStatus, number>,
} as const;

/** Progressive tax on a taxable-income figure. */
export function taxFromBrackets(taxableIncome: number, status: FilingStatus): number {
  if (taxableIncome <= 0) return 0;
  const brackets = TAX_PARAMS.brackets[status];
  let tax = 0;
  let floor = 0;
  for (const { upTo, rate } of brackets) {
    if (taxableIncome <= floor) break;
    const slice = Math.min(taxableIncome, upTo) - floor;
    if (slice > 0) tax += slice * rate;
    floor = upTo;
  }
  return tax;
}

/** Marginal rate at a given taxable income — used for the "next dollar" readout. */
export function marginalRate(taxableIncome: number, status: FilingStatus): number {
  const brackets = TAX_PARAMS.brackets[status];
  for (const { upTo, rate } of brackets) {
    if (taxableIncome <= upTo) return rate;
  }
  return brackets[brackets.length - 1].rate;
}
